import React, { useState, useEffect } from 'react';
import './CookieConsentBanner.css';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { CookiePolicyModal } from './CookiePolicyModal';

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentBannerProps {
  onConsentChange?: (preferences: ConsentPreferences) => void;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ 
  onConsentChange 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made an explicit consent choice
    const savedConsent = localStorage.getItem('cookieConsent');
    const hasExplicitConsent = localStorage.getItem('cookieConsentExplicit');
    
    if (!savedConsent || !hasExplicitConsent) {
      // Show banner if no explicit consent was given
      setIsVisible(true);
    } else {
      // Load saved preferences only if explicit consent exists
      try {
        const savedPreferences = JSON.parse(savedConsent);
        setPreferences(savedPreferences);
        onConsentChange?.(savedPreferences);
      } catch (error) {
        console.error('Error parsing saved consent:', error);
        // Clear corrupted data and show banner again
        localStorage.removeItem('cookieConsent');
        localStorage.removeItem('cookieConsentExplicit');
        setIsVisible(true);
      }
    }
  }, [onConsentChange]);

  const handleAcceptAll = () => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(necessaryOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (consentPreferences: ConsentPreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify(consentPreferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    localStorage.setItem('cookieConsentExplicit', 'true'); // Mark as explicit consent
    setIsVisible(false);
    onConsentChange?.(consentPreferences);
  };

  const handlePreferenceChange = (type: keyof ConsentPreferences, value: boolean) => {
    if (type === 'necessary') return; // Cannot disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: value,
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <button 
            className="cookie-consent-close"
            onClick={handleAcceptNecessary}
            title="Accept necessary cookies only"
          >
            ×
          </button>
          <div className="cookie-consent-header">
            <h3>🍪 We use cookies</h3>
            <p>
              Essential for site functionality, with optional analytics to improve your experience.
            </p>
          </div>

          {showDetails && (
            <div className="cookie-preferences">
              <div className="preference-item">
                <div className="preference-info">
                  <label className="preference-label">
                    <input
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled={true}
                      onChange={() => {}} // Disabled
                    />
                    <span className="checkmark required"></span>
                    <div>
                      <strong>Necessary Cookies</strong>
                      <p>Required for basic website functionality. Cannot be disabled.</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="preference-item">
                <div className="preference-info">
                  <label className="preference-label">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    <div>
                      <strong>Analytics Cookies</strong>
                      <p>Help us understand how visitors interact with our website (Google Analytics).</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="preference-item">
                <div className="preference-info">
                  <label className="preference-label">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    <div>
                      <strong>Marketing Cookies</strong>
                      <p>Used for targeted advertising and social media features (LinkedIn Insight).</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="cookie-consent-actions">
            {showDetails ? (
              <>
                <button
                  onClick={handleSavePreferences}
                  className="btn-save-preferences"
                >
                  Save Preferences
                </button>
                <button
                  onClick={() => setShowDetails(false)}
                  className="btn-back"
                >
                  Back
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleAcceptAll}
                  className="btn-accept-all"
                >
                  Accept All
                </button>
                <button
                  onClick={handleAcceptNecessary}
                  className="btn-necessary-only"
                >
                  Necessary Only
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className="btn-customize"
                >
                  Customize
                </button>
              </>
            )}
          </div>

          <div className="cookie-consent-links">
            <p>
              <button 
                className="link-button"
                onClick={() => setShowPrivacyModal(true)}
              >
                Privacy Policy
              </button>
              {' • '}
              <button 
                className="link-button"
                onClick={() => setShowCookieModal(true)}
              >
                Cookie Policy
              </button>
            </p>
          </div>
        </div>
      </div>
      
      {/* Policy Modals */}
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
      <CookiePolicyModal 
        isOpen={showCookieModal} 
        onClose={() => setShowCookieModal(false)} 
      />
    </div>
  );
};

export default CookieConsentBanner;